import { type TeamAnalysisMatches } from "@/api/tournamentAnalysis";
import { Loader } from "../../common/loader";
import dayjs from "dayjs";
import { getTeamLogo } from "@/lib/utils";

interface TournamentAnalysisRecentGamesCardProps {
    data?: TeamAnalysisMatches;
    isLoading?: boolean;
}

export function TournamentAnalysisRecentGamesCard({ data, isLoading }: TournamentAnalysisRecentGamesCardProps) {
    if (isLoading)
        return (
            <div
                className="bg-surface-container-lowest rounded-xl flex items-center justify-center shadow-sm"
                style={{ minHeight: 200 }}
            >
                <Loader message="Loading recent games..." />
            </div>
        );

    const upcomingMatch = data?.upcomingMatch;
    const lastMatches = data?.lastMatches || [];
    const hasUpcomingMatch = upcomingMatch !== undefined;
    const hasRecentMatches = lastMatches.length > 0;

    if (!data || (!hasUpcomingMatch && !hasRecentMatches)) {
        return (
            <div
                className="bg-surface-container-lowest rounded-xl flex items-center justify-center shadow-sm"
                style={{ minHeight: 200 }}
            >
                <p className="text-on-surface-variant">No recent games available</p>
            </div>
        );
    }

    const getResultBadgeColor = (result: string): string => {
        const r = result.toLowerCase();
        if (r === "win" || r === "win overtime" || r === "win penalties") return "var(--color-general-green-to)";
        if (r === "loss" || r === "loss overtime" || r === "loss penalties") return "var(--color-general-red)";
        return "var(--color-rank-silver)";
    };

    const getResultBadgeLabel = (result: string): string => {
        const r = result.toLowerCase();
        if (r === "win overtime") return "WIN OT";
        if (r === "loss overtime") return "LOSS OT";
        if (r === "win penalties") return "WIN PEN";
        if (r === "loss penalties") return "LOSS PEN";
        return result.toUpperCase();
    };

    const headerCells = ["Result", "Opponent", "Date", "Score"];
    const shortDateFormat = "DD/MM";

    return (
        <div
            id="onboarding-tournament-recent-games"
            className="bg-surface-container-lowest rounded-xl overflow-y-auto shadow-sm h-full"
        >
            <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                    <tr className="border-0">
                        {headerCells.map((label) => (
                            <th
                                key={label}
                                className="data-table-header px-4 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest text-left whitespace-nowrap"
                            >
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {hasUpcomingMatch && upcomingMatch && (
                        <tr className="data-table-row transition-colors border-0 odd:bg-surface-container-lowest even:bg-surface-container-low">
                            <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                    className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white"
                                    style={{ background: "var(--color-rank-silver)" }}
                                >
                                    Soon
                                </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-on-surface whitespace-nowrap">
                                vs {upcomingMatch.opponent}
                            </td>
                            <td className="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap">
                                {dayjs(upcomingMatch.date).format(shortDateFormat)}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-on-surface-variant whitespace-nowrap">—</td>
                        </tr>
                    )}
                    {lastMatches.map((match) => (
                        <tr
                            key={`${match.date}-${match.opponent}`}
                            className="data-table-row transition-colors border-0 odd:bg-surface-container-lowest even:bg-surface-container-low"
                        >
                            <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                    className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase text-white"
                                    style={{ background: getResultBadgeColor(match.result) }}
                                >
                                    {getResultBadgeLabel(match.result)}
                                </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-on-surface max-w-[200px] truncate hidden md:block">
                                {match.opponent}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-on-surface max-w-[90px] truncate block md:hidden">
                                <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm shrink-0">
                                    <img
                                        src={getTeamLogo(match.opponent)}
                                        alt={match.opponent}
                                        className="w-4 h-4 object-contain"
                                    />
                                </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap">
                                {dayjs(match.date).format(shortDateFormat)}
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-primary whitespace-nowrap">
                                {match.score}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
